import type { School } from '@kimuno/api-sdk';
import { borderRadius, palette, spaces } from '@kimuno/theme';
import {
	Autocomplete,
	Box,
	IconButton,
	InputAdornment,
	Popper,
	SvgIcon,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import getMuiIcon from '../../../utils/getMuiIcon';

interface SchoolAutocompleteProps {
	value: School;
	inputValue: string;
	options: School[];
	loading: boolean;
	onInputChange: (value: string) => void;
	onChange: (value: School) => void;
	placeholder: string;
	noOptionsText: string;
	loadingText: string;
	setValue: (value: School) => void;
	setInputValue: (value: string) => void;
}

const Menu = (props: any) => (
	<Popper
		{...props}
		modifiers={[
			{
				name: 'flip',
				options: {
					fallbackPlacements: ['bottom-start'],
				},
			},
		]}
		sx={{
			'& .MuiAutocomplete-paper': {
				marginTop: spaces.xxs,
				padding: spaces.xs,
				borderRadius: borderRadius.md,
				border: `1px solid ${palette.elevation.outlined}`,
				boxShadow: '0px 4px 20px rgba(44, 44, 44, 0.08)',
				'& .MuiAutocomplete-listbox': {
					'::-webkit-scrollbar': { display: 'none' },
					scrollbarWidth: 'none',
					msOverflowStyle: 'none',
				},
			},
		}}
	/>
);

const SchoolAutocomplete: React.FC<SchoolAutocompleteProps> = ({
	value,
	inputValue,
	options,
	loading,
	onInputChange,
	onChange,
	placeholder,
	noOptionsText,
	loadingText,
	setValue,
	setInputValue,
}) => {
	const { t } = useTranslation();
	const [inputFocused, setInputFocused] = useState(false);

	const getOptionLabelWithAddress = useCallback((option: School | string) => {
		if (typeof option === 'string') return option;
		const { name = '', formattedAddress = '' } = option;
		return formattedAddress ? `${name}, ${formattedAddress}` : name;
	}, []);

	return (
		<Autocomplete
			PopperComponent={Menu}
			disableClearable
			options={options}
			loading={loading}
			forcePopupIcon={false}
			noOptionsText={noOptionsText}
			loadingText={loadingText}
			filterOptions={(options) => options}
			getOptionLabel={getOptionLabelWithAddress}
			inputValue={inputValue}
			value={value}
			onInputChange={(_, value) => onInputChange(value)}
			onChange={(_, value) => onChange(value)}
			isOptionEqualToValue={(option, value) => option.id === value.id}
			renderOption={(props, option) => {
				// MUI puts the list `key` on `props`; the Tooltip is the array child, so it needs it.
				const { key, ...optionProps } = props;
				const optionLabel = getOptionLabelWithAddress(option);
				const matches = match(optionLabel, inputValue, { insideWords: true });
				const parts = parse(optionLabel, matches);

				return (
					<Tooltip key={key} title={optionLabel} arrow>
						<Box
							component="li"
							data-testid={option.id}
							{...optionProps}
							sx={{
								borderRadius: borderRadius.sm,
								cursor: 'pointer',
								color: palette.text.primary,
								display: 'list-item!important',
								minHeight: 'auto!important',
								fontWeight: 400,
								letterSpacing: '0.15px',
								overflow: 'hidden',
								textOverflow: 'ellipsis',
								whiteSpace: 'nowrap',
								'&:hover, &[aria-selected="true"]': {
									backgroundColor: palette.primary.selected,
								},
							}}
						>
							<SvgIcon
								component={getMuiIcon('location')}
								sx={{
									color: palette.text.secondary,
									width: 24,
									height: 24,
									marginRight: spaces.sm,
								}}
							/>
							{parts.map((part, index) => (
								<Typography
									// biome-ignore lint/suspicious/noArrayIndexKey: index keys are pre-existing; switching the key source changes reconciliation behavior
									key={index}
									component="span"
									sx={{
										fontWeight: part.highlight ? 700 : 400,
										color: palette.text.primary,
										overflow: 'hidden',
										textOverflow: 'ellipsis',
										whiteSpace: 'nowrap',
									}}
								>
									{part.text}
								</Typography>
							))}
						</Box>
					</Tooltip>
				);
			}}
			renderInput={(params) => (
				<Tooltip
					title={value && value.name ? getOptionLabelWithAddress(value) : ''}
					arrow
					disableHoverListener={!value || !value.name}
				>
					<TextField
						{...params}
						placeholder={placeholder}
						onFocus={() => setInputFocused(true)}
						onBlur={() => setInputFocused(false)}
						InputProps={{
							...params.InputProps,
							endAdornment: (
								<>
									<InputAdornment position="end">
										{inputValue && value && inputFocused && (
											<IconButton
												data-testid="clear-button"
												onClick={() => {
													setInputValue('');
													setValue({
														id: '',
														name: '',
														formattedAddress: '',
														state: '',
													});
												}}
												aria-label={t('Clear selection')}
											>
												<SvgIcon
													component={getMuiIcon('close')}
													sx={{
														width: 20,
														height: 20,
														color: palette.text.secondary,
													}}
												/>
											</IconButton>
										)}
										<Box>
											<SvgIcon
												component={getMuiIcon('location')}
												sx={{
													color: palette.text.secondary,
													width: 20,
													height: 20,
												}}
											/>
										</Box>
									</InputAdornment>
								</>
							),
						}}
						sx={{
							'& .MuiInputBase-root': {
								display: 'flex',
								paddingX: spaces.md,
								paddingY: 0,
								borderRadius: borderRadius.full,
								background: palette.background.paperElevation0,
							},
							'& .MuiInputBase-input': {
								width: '100%',
								color: palette.text.primary,
								textOverflow: 'ellipsis',
								fontWeight: 400,
								letterSpacing: '0.15px',
							},
						}}
					/>
				</Tooltip>
			)}
		/>
	);
};

export default React.memo(SchoolAutocomplete);
